import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './GiftReveal.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GiftReveal = () => {
  const { giftToken } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    loadGift();
  }, [giftToken]);

  const loadGift = async () => {
    try {
      const response = await axios.get(`${API}/gift/${giftToken}`);
      setGift(response.data);
      setClaimed(response.data.gift_claimed);
    } catch (err) {
      setError('Geschenk nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = () => {
    setRevealed(true);
  };

  const handleClaim = async () => {
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { returnTo: `/gift/${giftToken}`, message: 'Melde dich an, um das Geschenk zu beanspruchen' }
      });
      return;
    }

    setClaiming(true);
    try {
      await axios.post(`${API}/gift/${giftToken}/claim`, {}, { withCredentials: true });
      setClaimed(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Beanspruchen');
    } finally {
      setClaiming(false);
    }
  };

  const getCandleSize = (amount) => {
    if (amount === 200) return 'kleine';
    if (amount === 500) return 'mittlere';
    return 'große';
  };

  if (loading) {
    return (
      <div className="gift-page">
        <div className="gift-loading">
          <div className="gift-spinner"></div>
          <p>Lade Geschenk...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gift-page">
        <div className="gift-error">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Zur Startseite</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gift-page">
      <div className="gift-ambient"></div>
      
      {!revealed ? (
        <div className="gift-box-container">
          <div className="gift-box" onClick={handleReveal}>
            <div className="gift-ribbon"></div>
            <div className="gift-bow"></div>
            <div className="gift-sparkles">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="sparkle" style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}></div>
              ))}
            </div>
          </div>
          <p className="gift-hint">Tippe, um dein Geschenk zu öffnen</p>
        </div>
      ) : (
        <div className="gift-revealed">
          <div className="gift-flame-container">
            <div className="gift-flame">
              <div className="flame-inner"></div>
            </div>
            <div className="gift-glow"></div>
          </div>

          <h1 className="gift-title">Ein Licht für dich</h1>
          
          {gift.recipient_name && (
            <p className="gift-recipient">Liebe/r {gift.recipient_name},</p>
          )}
          
          <div className="gift-candle-info">
            <p className="gift-candle-name">🕯️ "{gift.name}"</p>
            <p className="gift-candle-size">Eine {getCandleSize(gift.amount)} Kerze</p>
          </div>

          {gift.gift_message && (
            <div className="gift-message-box">
              <p>"{gift.gift_message}"</p>
            </div>
          )}

          {gift.message && (
            <div className="gift-candle-message">
              <p className="message-label">Nachricht der Kerze:</p>
              <p>"{gift.message}"</p>
            </div>
          )}

          <div className="gift-actions">
            {claimed ? (
              <div className="gift-claimed-message">
                <p>✨ Dieses Geschenk wurde beansprucht</p>
                <button onClick={() => navigate('/dashboard')}>
                  Meine Kerzen ansehen
                </button>
              </div>
            ) : (
              <>
                <button 
                  className="gift-claim-button"
                  onClick={handleClaim}
                  disabled={claiming}
                >
                  {claiming ? 'Wird beansprucht...' : 'Geschenk meinem Konto hinzufügen'}
                </button>
                {!isAuthenticated && (
                  <p className="gift-login-hint">Du musst angemeldet sein, um das Geschenk zu beanspruchen</p>
                )}
              </>
            )}
            
            <button 
              className="gift-view-button"
              onClick={() => navigate('/')}
            >
              Im Lichtfeld ansehen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftReveal;
